import React, { useState, useEffect, useCallback } from 'react';

function parseNonNegInt(str, fallback = 0) {
  if (str === '' || str === null || str === undefined) return fallback;
  const n = parseInt(str, 10);
  return Number.isNaN(n) ? fallback : Math.max(0, n);
}

/**
 * Input recupero in minuti + secondi; il valore persistito resta un unico numero (secondi).
 *
 * @param {string} [layout] - "default" | "templateGrid". templateGrid: gruppo compatto (min + sec) largo quanto il contenuto.
 * @param {'xs'|'sm'} [templateFieldSize] - Solo layout templateGrid: allinea text-xs (crea template) o text-sm (modifica template).
 */
export default function RecoveryMinSecInputs({
  valueSeconds,
  onSecondsChange,
  defaultTotalSeconds = 90,
  disabled = false,
  compact = false,
  focusColor = 'indigo',
  layout = 'default',
  templateFieldSize = 'xs',
  /** Sfondo input in dark allineato a gymPageTheme (gray-800) */
  gymDarkInputs = false,
}) {
  const ring =
    focusColor === 'emerald'
      ? 'focus:border-emerald-500 focus:ring-emerald-500 dark:focus:border-indigo-500 dark:focus:ring-indigo-500/30'
      : 'focus:border-[#372d83] focus:ring-[#372d83]/30 dark:focus:border-indigo-500 dark:focus:ring-indigo-500/30';

  const darkField = gymDarkInputs
    ? 'dark:border-gray-700 dark:bg-gray-800'
    : 'dark:border-gray-600 dark:bg-gray-700';

  const lightField = gymDarkInputs
    ? 'border border-[#dad8e5] bg-white text-[#3a3a3a]'
    : 'border border-gray-300 bg-white text-gray-900';

  const mainLabelClass = compact
    ? `text-xs block mb-1 ${gymDarkInputs ? 'text-gray-500 dark:text-gray-300/90' : 'text-gray-500 dark:text-gray-400'}`
    : `block text-sm font-medium mb-1 ${gymDarkInputs ? 'text-gray-700 dark:text-white/85' : 'text-gray-700 dark:text-gray-300'}`;

  const subLabelClass = gymDarkInputs
    ? 'text-xs text-gray-500 dark:text-white/60 block mt-1'
    : 'text-xs text-gray-500 dark:text-gray-400 block mt-1';

  /* Nasconde gli spin-button nativi: su campi stretti tagliano il numero */
  const noSpinner =
    '[appearance:textfield] [-moz-appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none';

  const inputClass = compact
    ? `w-full min-w-[3.75rem] px-2 py-1.5 text-xs rounded tabular-nums focus:ring-1 ${ring} disabled:opacity-50 ${noSpinner} ${lightField} dark:text-white ${darkField}`
    : `w-full min-w-[4.25rem] px-3 py-2 rounded-md tabular-nums focus:ring-2 ${ring} disabled:opacity-50 ${noSpinner} ${lightField} dark:text-white ${darkField}`;

  const pairRowClass = compact ? 'gap-2' : 'gap-3';

  const toTotal = (mStr, sStr) => {
    const m = mStr === '' ? 0 : parseNonNegInt(mStr, 0);
    if (mStr !== '' && Number.isNaN(parseInt(mStr, 10))) return null;
    let s = 0;
    if (sStr !== '') {
      const p = parseInt(sStr, 10);
      if (Number.isNaN(p)) return null;
      s = Math.max(0, p);
    }
    return m * 60 + s;
  };

  const [minStr, setMinStr] = useState('');
  const [secStr, setSecStr] = useState('');

  useEffect(() => {
    if (valueSeconds === '' || valueSeconds === null || valueSeconds === undefined) {
      setMinStr('');
      setSecStr('');
      return;
    }
    const n = parseInt(valueSeconds, 10);
    if (Number.isNaN(n) || n < 0) {
      setMinStr('');
      setSecStr('');
      return;
    }
    setMinStr(String(Math.floor(n / 60)));
    setSecStr(String(n % 60));
  }, [valueSeconds]);

  const normalizeAndCommit = useCallback(() => {
    let m = parseNonNegInt(minStr, 0);
    let s = secStr === '' ? 0 : parseInt(secStr, 10);
    if (Number.isNaN(s)) s = 0;
    s = Math.max(0, s);
    m += Math.floor(s / 60);
    s = s % 60;
    let total = m * 60 + s;
    if (total === 0 && defaultTotalSeconds != null) {
      total = defaultTotalSeconds;
    }
    setMinStr(String(Math.floor(total / 60)));
    setSecStr(String(total % 60));
    onSecondsChange(total);
  }, [minStr, secStr, defaultTotalSeconds, onSecondsChange]);

  const handleMinChange = (e) => {
    const v = e.target.value;
    setMinStr(v);
    const t = toTotal(v, secStr);
    if (t !== null) onSecondsChange(t);
  };

  const handleSecChange = (e) => {
    const v = e.target.value;
    setSecStr(v);
    const t = toTotal(minStr, v);
    if (t !== null) onSecondsChange(t);
  };

  if (layout === 'templateGrid') {
    const tplRing = focusColor === 'emerald' ? 'focus:ring-emerald-500' : 'focus:ring-indigo-500';
    const tplText = templateFieldSize === 'sm' ? 'h-9 text-sm' : 'text-xs py-1.5';
    const tplDarkField = gymDarkInputs
      ? 'dark:border-gray-700 dark:bg-gray-800'
      : 'dark:border-gray-600 dark:bg-gray-700';
    const tplInputClass = `w-16 shrink-0 px-1.5 text-center ${tplText} border border-[#dad8e5] rounded bg-white text-[#3a3a3a] tabular-nums focus:ring-1 ${tplRing} focus:border-transparent disabled:opacity-50 dark:text-white ${noSpinner} ${tplDarkField}`;
    const tplLabelClass = gymDarkInputs
      ? 'text-xs text-gray-500 dark:text-white/65'
      : 'text-xs text-gray-500 dark:text-gray-400';

    return (
      <div className="min-w-0">
        <span className={`${tplLabelClass} mb-1 block`}>Recupero</span>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-1.5">
            <input
              type="number"
              min={0}
              step={1}
              disabled={disabled}
              value={minStr}
              onChange={handleMinChange}
              onBlur={normalizeAndCommit}
              className={tplInputClass}
              aria-label="Minuti di recupero"
            />
            <span className={`${tplLabelClass} shrink-0`}>min</span>
          </div>
          <div className="flex items-center gap-1.5">
            <input
              type="number"
              min={0}
              step={1}
              disabled={disabled}
              value={secStr}
              onChange={handleSecChange}
              onBlur={normalizeAndCommit}
              className={tplInputClass}
              aria-label="Secondi di recupero"
            />
            <span className={`${tplLabelClass} shrink-0`}>sec</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={compact ? 'min-w-0' : 'min-w-0 md:min-w-[12rem]'}>
      <span className={mainLabelClass}>Recupero</span>
      <div className={`flex ${pairRowClass} items-start`}>
        <div className="min-w-0 flex-1 flex flex-col">
          <input
            type="number"
            min={0}
            step={1}
            disabled={disabled}
            value={minStr}
            onChange={handleMinChange}
            onBlur={normalizeAndCommit}
            className={inputClass}
            aria-label="Minuti di recupero"
          />
          <label className={subLabelClass}>Min</label>
        </div>
        <div className="min-w-0 flex-1 flex flex-col">
          <input
            type="number"
            min={0}
            step={1}
            disabled={disabled}
            value={secStr}
            onChange={handleSecChange}
            onBlur={normalizeAndCommit}
            className={inputClass}
            aria-label="Secondi di recupero"
          />
          <label className={subLabelClass}>Sec</label>
        </div>
      </div>
    </div>
  );
}
